/**
 * Track Effect Chain Component
 * Compact effect chain display for mixer channel strips
 * Features: Drag-and-drop reordering, enable/bypass toggle, persistence-ready
 */

import { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { DropdownPortal } from '../../ui/dropdown-portal';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import {
  Plus,
  X,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Waves,
  Activity,
  Timer,
  Sliders,
  Music2,
  Zap,
  Power,
} from 'lucide-react';
import { cn } from '../../../lib/utils';

// Effect type definitions with icons and colors
export const EFFECT_TYPES = {
  compressor: {
    name: 'Comp',
    fullName: 'Compressor',
    icon: Activity,
    color: 'from-red-500 to-orange-500',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/50',
    activeGlow: 'shadow-red-500/30',
    description: 'Dynamic range control',
    defaultParams: {
      threshold: { value: -24, min: -60, max: 0, unit: 'dB' },
      ratio: { value: 4, min: 1, max: 20, unit: ':1' },
      attack: { value: 0.003, min: 0, max: 0.1, unit: 's' },
      release: { value: 0.25, min: 0.01, max: 1, unit: 's' },
    },
  },
  eq: {
    name: 'EQ',
    fullName: '3-Band EQ',
    icon: Sliders,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/50',
    activeGlow: 'shadow-blue-500/30',
    description: '3-band parametric EQ',
    defaultParams: {
      lowGain: { value: 0, min: -12, max: 12, unit: 'dB' },
      midGain: { value: 0, min: -12, max: 12, unit: 'dB' },
      highGain: { value: 0, min: -12, max: 12, unit: 'dB' },
    },
  },
  reverb: {
    name: 'Verb',
    fullName: 'Reverb',
    icon: Waves,
    color: 'from-purple-500 to-indigo-500',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-500/50',
    activeGlow: 'shadow-purple-500/30',
    description: 'Space and ambience',
    defaultParams: {
      decay: { value: 2, min: 0.1, max: 10, unit: 's' },
      mix: { value: 0.3, min: 0, max: 1, unit: '' },
    },
  },
  delay: {
    name: 'Dly',
    fullName: 'Delay',
    icon: Timer,
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/50',
    activeGlow: 'shadow-emerald-500/30',
    description: 'Echo effect',
    defaultParams: {
      time: { value: 0.3, min: 0.01, max: 2, unit: 's' },
      feedback: { value: 0.4, min: 0, max: 0.95, unit: '' },
      mix: { value: 0.3, min: 0, max: 1, unit: '' },
    },
  },
  chorus: {
    name: 'Chor',
    fullName: 'Chorus',
    icon: Music2,
    color: 'from-pink-500 to-rose-500',
    bgColor: 'bg-pink-500/20',
    borderColor: 'border-pink-500/50',
    activeGlow: 'shadow-pink-500/30',
    description: 'Thickening modulation',
    defaultParams: {
      rate: { value: 0.5, min: 0.1, max: 5, unit: 'Hz' },
      depth: { value: 0.002, min: 0, max: 0.01, unit: '' },
      mix: { value: 0.5, min: 0, max: 1, unit: '' },
    },
  },
  flanger: {
    name: 'Flng',
    fullName: 'Flanger',
    icon: Zap,
    color: 'from-amber-500 to-yellow-500',
    bgColor: 'bg-amber-500/20',
    borderColor: 'border-amber-500/50',
    activeGlow: 'shadow-amber-500/30',
    description: 'Sweeping modulation',
    defaultParams: {
      rate: { value: 0.25, min: 0.05, max: 5, unit: 'Hz' },
      depth: { value: 0.002, min: 0, max: 0.005, unit: '' },
      feedback: { value: 0.5, min: 0, max: 0.95, unit: '' },
    },
  },
};

/**
 * Create a new effect instance
 */
export const createEffect = (type) => {
  const effectDef = EFFECT_TYPES[type];
  if (!effectDef) return null;
  
  const params = {};
  Object.entries(effectDef.defaultParams).forEach(([key, def]) => {
    params[key] = def.value;
  });
  
  return {
    id: `fx-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type,
    enabled: true,
    params,
  };
};

/**
 * Draggable Effect Item
 * Shows effect with drag handle, bypass toggle, and remove button
 */
const DraggableEffectItem = ({ 
  effect, 
  onToggle, 
  onRemove, 
  onSelect, 
  isSelected,
  isDragging 
}) => {
  const effectDef = EFFECT_TYPES[effect.type];
  if (!effectDef) return null;
  
  const IconComponent = effectDef.icon;
  
  return (
    <motion.div
      layout
      className={cn(
        "relative group flex items-center gap-1 px-1 py-0.5 rounded text-[9px] font-medium transition-all",
        effect.enabled ? effectDef.bgColor : 'bg-zinc-800/80',
        effect.enabled ? effectDef.borderColor : 'border-zinc-600/50',
        "border",
        isSelected && "ring-1 ring-white/50",
        isDragging && "shadow-lg scale-105 z-50",
        effect.enabled && `shadow-sm ${effectDef.activeGlow}`
      )}
      data-testid={`effect-item-${effect.id}`}
    >
      {/* Drag Handle */}
      <div 
        className={cn(
          "cursor-grab active:cursor-grabbing p-0.5 -ml-0.5 rounded",
          "opacity-50 group-hover:opacity-100 transition-opacity",
          isDragging && "opacity-100"
        )}
        data-testid={`effect-drag-handle-${effect.id}`}
      >
        <GripVertical className="w-2.5 h-2.5 text-zinc-400" />
      </div>
      
      {/* Effect Icon & Name */}
      <div 
        className={cn(
          "flex items-center gap-1 cursor-pointer",
          effect.enabled ? "text-white" : "text-zinc-500"
        )}
        onClick={() => onSelect?.(effect)}
      >
        <IconComponent className="w-3 h-3" />
        <span>{effectDef.name}</span>
      </div>
      
      {/* Bypass Indicator (strikethrough when bypassed) */}
      {!effect.enabled && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-full h-px bg-zinc-500 rotate-[-15deg]" />
        </div>
      )}
      
      {/* Action Buttons (visible on hover) */}
      <div className={cn(
        "flex items-center gap-0.5 ml-1",
        "opacity-0 group-hover:opacity-100 transition-opacity",
        isDragging && "opacity-0"
      )}>
        {/* Bypass/Enable Toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggle?.(effect.id); }}
          className={cn(
            "w-4 h-4 rounded flex items-center justify-center transition-all",
            effect.enabled 
              ? "bg-emerald-500 hover:bg-emerald-400" 
              : "bg-zinc-600 hover:bg-zinc-500"
          )}
          title={effect.enabled ? "Bypass" : "Enable"}
          data-testid={`effect-bypass-${effect.id}`}
        >
          <Power className="w-2.5 h-2.5 text-white" />
        </button>
        
        {/* Remove Button */}
        <button
          onClick={(e) => { e.stopPropagation(); onRemove?.(effect.id); }}
          className="w-4 h-4 rounded bg-red-500/80 hover:bg-red-500 flex items-center justify-center transition-all"
          title="Remove effect"
          data-testid={`effect-remove-${effect.id}`}
        >
          <X className="w-2.5 h-2.5 text-white" />
        </button>
      </div>
    </motion.div>
  );
};

/**
 * Add Effect Dropdown (Compact) - Portal-based to avoid overflow clipping
 */
const AddEffectDropdown = ({ onAdd, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  
  return (
    <div className={cn("relative", className)}>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-5 h-5 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
        title="Add Effect"
        data-testid="add-effect-btn"
      >
        <Plus className="w-3 h-3 text-zinc-400" />
      </button>
      
      <DropdownPortal
        triggerRef={triggerRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        width={144}
        data-testid="add-effect-dropdown"
      >
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl overflow-hidden" data-testid="add-effect-dropdown">
          {Object.entries(EFFECT_TYPES).map(([type, info]) => {
            const IconComponent = info.icon;
            return (
              <button
                key={type}
                onClick={() => {
                  onAdd(type);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-800 transition-colors text-left"
                data-testid={`add-effect-option-${type}`}
              >
                <div className={cn(
                  "w-5 h-5 rounded flex items-center justify-center bg-gradient-to-br",
                  info.color
                )}>
                  <IconComponent className="w-3 h-3 text-white" />
                </div>
                <div>
                  <div className="text-[10px] font-medium text-white">{info.fullName}</div>
                </div>
              </button>
            );
          })}
        </div>
      </DropdownPortal>
    </div>
  );
};

/**
 * Effect Parameters Editor (Expanded View)
 */
const EffectParamsEditor = ({ effect, onChange, onClose }) => {
  const effectDef = EFFECT_TYPES[effect.type];
  if (!effectDef) return null;
  
  const IconComponent = effectDef.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="overflow-hidden"
    >
      <div className={cn(
        "mt-2 p-2 rounded-lg border",
        effectDef.bgColor,
        effectDef.borderColor
      )}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <IconComponent className="w-3 h-3 text-white" />
            <span className="text-[10px] font-semibold text-white">{effectDef.fullName}</span>
          </div>
          <button
            onClick={onClose}
            className="w-4 h-4 rounded bg-black/20 hover:bg-black/40 flex items-center justify-center transition-colors"
          >
            <X className="w-3 h-3 text-white/70" />
          </button>
        </div>
        
        <div className="space-y-1.5">
          {Object.entries(effectDef.defaultParams).map(([paramName, paramDef]) => (
            <div key={paramName} className="flex items-center gap-2">
              <span className="text-[9px] text-white/70 w-12 truncate capitalize">
                {paramName.replace(/([A-Z])/g, ' $1').trim()}
              </span>
              <Slider
                value={[effect.params[paramName] ?? paramDef.value]}
                min={paramDef.min}
                max={paramDef.max}
                step={(paramDef.max - paramDef.min) / 100}
                onValueChange={([v]) => onChange(effect.id, paramName, v)}
                className="flex-1 h-4"
              />
              <span className="text-[8px] text-white/50 w-10 text-right font-mono">
                {(effect.params[paramName] ?? paramDef.value).toFixed(2)}{paramDef.unit}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Main Track Effect Chain Component
 */
export const TrackEffectChain = ({
  trackId,
  effects = [],
  onAddEffect,
  onRemoveEffect,
  onToggleEffect,
  onReorderEffects,
  onParamChange,
  isExpanded = false,
  onToggleExpand,
  className,
}) => {
  const [selectedEffectId, setSelectedEffectId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  
  const handleAddEffect = (type) => {
    const newEffect = createEffect(type);
    if (newEffect) {
      onAddEffect?.(trackId, newEffect);
    }
  };
  
  const handleReorder = (newOrder) => {
    onReorderEffects?.(trackId, newOrder);
  };
  
  const selectedEffect = effects.find(e => e.id === selectedEffectId);
  
  // Count enabled effects for badge
  const enabledCount = effects.filter(e => e.enabled).length;
  
  return (
    <div className={cn("w-full", className)} data-testid={`effect-chain-${trackId}`}>
      {/* Chain Header */}
      <div className="flex items-center justify-between mb-1">
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-1 text-[9px] text-zinc-400 hover:text-white transition-colors"
          data-testid={`effect-chain-toggle-${trackId}`}
        >
          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          <span>FX</span>
          {effects.length > 0 && (
            <span className={cn(
              "px-1 rounded text-[8px]",
              enabledCount > 0 ? "bg-cyan-500/30 text-cyan-300" : "bg-zinc-700 text-zinc-400"
            )}>
              {enabledCount}/{effects.length}
            </span>
          )}
        </button>
        <AddEffectDropdown onAdd={handleAddEffect} />
      </div>
      
      {/* Effect Chain (Draggable) */}
      {effects.length > 0 ? (
        <Reorder.Group 
          axis="x" 
          values={effects} 
          onReorder={handleReorder}
          className="flex flex-wrap gap-1"
          data-testid={`effect-chain-list-${trackId}`}
        >
          {effects.map((effect) => (
            <Reorder.Item 
              key={effect.id} 
              value={effect}
              onDragStart={() => setDraggingId(effect.id)}
              onDragEnd={() => setDraggingId(null)}
              whileDrag={{ scale: 1.05, zIndex: 50 }}
              className="cursor-grab active:cursor-grabbing"
            >
              <DraggableEffectItem
                effect={effect}
                onToggle={onToggleEffect}
                onRemove={(id) => onRemoveEffect?.(trackId, id)}
                onSelect={(e) => setSelectedEffectId(e.id === selectedEffectId ? null : e.id)}
                isSelected={effect.id === selectedEffectId}
                isDragging={draggingId === effect.id}
              />
            </Reorder.Item>
          ))}
        </Reorder.Group>
      ) : (
        <div className="text-[8px] text-zinc-600 text-center py-1">
          No effects - click + to add
        </div>
      )}
      
      {/* Selected Effect Editor */}
      <AnimatePresence>
        {isExpanded && selectedEffect && (
          <EffectParamsEditor
            effect={selectedEffect}
            onChange={onParamChange}
            onClose={() => setSelectedEffectId(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrackEffectChain;
